import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonService } from 'src/app/services/common.service';
import { ConfigService } from 'src/app/services/config.service';
import { EcryptServiceService } from 'src/app/services/ecrypt-service.service';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { Constant } from 'src/app/constant/constant';

declare var Stripe: any;

@Component({
  selector: 'calsoft-stripe',
  templateUrl: './stripe.component.html',
  styleUrls: ['./stripe.component.scss']
})
export class StripeComponent implements OnInit, OnDestroy {

  sanitizedUrl: string = '';

  url = '';


  stripe: any;
  card: any;
  loading: boolean = false;
  status: boolean = false;
  public stripeAPIKey: any;
  storeName: any;

  @Input() finalAmount: any;
  @Input() data: any;
  @Input() order: any;
  @Input() configData: any;
  @Input() partialAmountEnabled: any;
  @Input() contact: any;
  @Input() currency: string = 'USD';
  @Input() currencySymbol: string = '$';
  @Input() disable: boolean = false;

  @Input() form: FormGroup;
  @Output() valide: EventEmitter<any> = new EventEmitter<any>();

  @Output() paymentStatus: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private ecryptService: EcryptServiceService
    , private router: Router) {
    this.url = this.router.url || '';
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy))
      .subscribe(() => { this.url = this.router.url || ''; });
  }

  ngOnInit() {
    this.getConfigdata();
  }

  getConfigdata() {
    this.configService.getKey().subscribe(
      (response: any) => {
        let configData = response;
        if (response.encryptedText != null) {
          const decrypted = this.ecryptService.decryptionAES(response.encryptedText);
          configData = JSON.parse(decrypted);
        }
        this.stripeAPIKey = configData['stripeKey'];
        this.storeName = configData['storeName'];
        this.status = true;
        this.initializeStripe();
      }, error => {
        this.stripeAPIKey = 'pk_test_51N0LspSHOnsu4Vo5LqXCUvWxTvC74Y2QGnbHAdpTh47tpOayHliBKVKWV1UOKNP0QJBXiK7s555T7f4I4PUVnxbl00VE4ysYmN';
        this.status = true;
        this.initializeStripe();
      }
    );
  }

  initializeStripe() {
    if (!window.document.getElementById('stripe-v3-script')) {
      const script = window.document.createElement('script');
      script.id = 'stripe-v3-script';
      script.type = 'text/javascript';
      script.src = 'https://js.stripe.com/v3/';
      script.onload = () => {
        this.setupElements();
      };
      window.document.body.appendChild(script);
    } else {
      // Script already exists, wait for it to be ready
      const checkStripe = setInterval(() => {
        if (typeof Stripe !== 'undefined') {
          clearInterval(checkStripe);
          this.setupElements();
        }
      }, 100);
    }
  }

  setupElements() {
    if (typeof Stripe === 'undefined') return;

    this.stripe = Stripe(this.stripeAPIKey || 'pk_test_51N0LspSHOnsu4Vo5LqXCUvWxTvC74Y2QGnbHAdpTh47tpOayHliBKVKWV1UOKNP0QJBXiK7s555T7f4I4PUVnxbl00VE4ysYmN');
    const elements = this.stripe.elements();

    const style = {
      base: {
        color: '#1a1a1a',
        fontFamily: 'Inter, "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#94a3b8'
        }
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444'
      }
    };

    this.card = elements.create('card', {
      style: style,
      hidePostalCode: true
    });

    // Check if element exists before mounting
    const cardEl = document.getElementById('card-element');
    if (cardEl) {
      this.card.mount('#card-element');
    } else {
      // Retry after a short delay if view not rendered
      setTimeout(() => this.card.mount('#card-element'), 500);
    }

    this.card.addEventListener('change', (event: any) => {
      const displayError = document.getElementById('card-errors');
      if (displayError) {
        if (event.error) {
          displayError.textContent = event.error.message;
        } else {
          displayError.textContent = '';
        }
      }
    });
  }

  async makePayment() {
    if (this.loading || !this.stripe || !this.card) return;

    if (this.form && !this.form.valid) {
      this.valide.emit("notValid");
      return;
    }

    this.loading = true;

    try {
      const result = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.card,
      });

      const { paymentMethod, error } = result;

      if (error) {
        const errorElement = document.getElementById('card-errors');
        if (errorElement) errorElement.textContent = error.message;
        this.loading = false;
        this.snackBar.open(error.message, '×', {
          panelClass: 'error',
          verticalPosition: 'bottom',
          duration: 3000
        });
      } else if (paymentMethod && paymentMethod.id) {
        this.processPayment(paymentMethod.id);
      } else {
        console.error('Stripe result missing paymentMethod or ID:', result);
        this.loading = false;
        this.snackBar.open('Payment method creation failed. Please try again.', '×', {
          panelClass: 'error',
          verticalPosition: 'bottom',
          duration: 3000
        });
      }
    } catch (err) {
      console.error('Error in Stripe makePayment:', err);
      this.loading = false;
      this.snackBar.open('An unexpected error occurred. Please try again.', '×', {
        panelClass: 'error',
        verticalPosition: 'bottom',
        duration: 3000
      });
    }
  }

  processPayment(paymentMethodId: any) {
    const object = {
      amount: Math.round(this.finalAmount) * 100,
      currency: (this.currency || 'USD').toLowerCase(),
      hash: paymentMethodId
    };

    const url = environment.serverAPI4 + "/api/stripe/payment-details";

    this.http.post<any>(url, object).subscribe(
      res => {
        if (res['status_code'] == Constant.RESPONSE_SUCCESS) {
          this.loading = false;
          this.emitResponse(res['data']);
        }
        // else if (res['data'] && res['data']['status'] === 'requires_action') {
        //   this.handleAction(res['data']['client_secret']);
        // }

        else {
          this.loading = false;
          this.snackBar.open(res['status_message'] || 'Payment failed. Please try again.', '×',
            { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
          //  this.emitResponse(res['data'] || { status: 'failed', id: 'failed' });
        }
      },
      err => {
        this.loading = false;
        this.snackBar.open('Error processing payment. Please check your connectivity.', '×',
          { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
      }
    );
  }

  async handleAction(clientSecret) {
    const { paymentIntent, error } = await this.stripe.handleCardAction(clientSecret);

    if (error) {
      this.loading = false;
      this.snackBar.open(error.message, '×', {
        panelClass: 'error',
        verticalPosition: 'bottom',
        duration: 3000
      });
    } else if (paymentIntent && paymentIntent.status === 'requires_confirmation') {
      // Backend confirmed it? Wait, confirm=true usually handles confirmation.
      // If handleCardAction is successful, the PaymentIntent status is typically 'requires_confirmation' only if manual confirmation flow.
      // But 'handleCardAction' is for 'requires_action'. 
      // After 'handleCardAction', the status should be checked. If we used automatic confirmation on server (confirm=true), 
      // we might need to call retrievePaymentIntent to see final status, OR confirm it again?
      // Let's assume after handleCardAction we need to notify backend or just check status.
      // Actually for confirm=true, we should call confirmCardPayment instead of handleCardAction to finalize everything in one go? 
      // No, confirmCardPayment takes payment_method data. We already attached it on backend.
      // So we can use confirmCardPayment with just clientSecret? Yes.
      this.confirmPayment(clientSecret);
    } else {
      // Success or some other state
      // Let's check status
      if (paymentIntent.status === 'succeeded') {
        this.loading = false;
        this.emitResponse(paymentIntent);
      } else {
        // Verify with backend or re-confirm
        this.confirmPayment(clientSecret);
      }
    }
  }

  async confirmPayment(clientSecret) {
    const { paymentIntent, error } = await this.stripe.confirmCardPayment(clientSecret);

    this.loading = false;

    if (error) {
      this.snackBar.open(error.message, '×', {
        panelClass: 'error',
        verticalPosition: 'bottom',
        duration: 3000
      });
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      this.emitResponse(paymentIntent);
    } else {
      let msg = 'Payment incomplete. Status: ' + paymentIntent.status;
      if (paymentIntent.last_payment_error && paymentIntent.last_payment_error.message) {
        msg = paymentIntent.last_payment_error.message;
      } else if (paymentIntent.amount_details && paymentIntent.amount_details.error) {
        msg = paymentIntent.amount_details.error;
      }

      this.snackBar.open(msg, '×', {
        panelClass: 'error',
        verticalPosition: 'bottom',
        duration: 3000
      });
    }
  }

  emitResponse(response: any) {
    const paymentInformation = {
      method: 'Stripe',
      type: response.object,
      payment_id: response.id,
      email: response.receipt_email || (this.contact && this.contact.email ? this.contact.email : ''),
      status: response.status,
      currency: response.currency,
      amount: response.amount,
      ...(this.partialAmountEnabled == 1 && { Partial_Paid_Amount: this.finalAmount })
    };

    const additionData = JSON.stringify(paymentInformation);
    const paymentObject = {
      salesPaymentTransaction: [{
        txn_id: response.id,
        txn_type: "customer",
        isClose: true
      }],
      salesFlatOrderPayment: {
        method: 'Stripe',
        additionalInformation: additionData
      },
      paypalPaymentStatus: response.status
    };

    this.paymentStatus.emit(paymentObject);
  }

  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

  protected _onDestroy = new Subject<void>();

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
